// services/InterviewGuardService.js
import { db } from "./firebase"
import { doc, updateDoc, onSnapshot } from "firebase/firestore"

const REPORT_URL     = "https://hire100x-e1dd4.uc.r.appspot.com/interviewguard/record"
const LAUNCH_TIMEOUT = 90_000 // give up after 90s if app never verifies

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateChallenge() {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("")
}

// ── Service ───────────────────────────────────────────────────────────────────

class InterviewGuardService {
  constructor() {
    this.meetingId          = null
    this.userRole           = null

    // Shared
    this.unsubscribe        = null

    // Candidate-side
    this._timeoutTimer      = null
    this._challenge         = null

    // Callbacks
    this.onAlertCallback    = null // (message: string) => void
    this.onClearCallback    = null // () => void
    this.onVerifiedCallback = null // () => void — app verified, monitoring started
    this.onReadyCallback    = null // () => void — candidate: verification complete
    this.onErrorCallback    = null // (reason: string) => void
  }

  // ── Public API ────────────────────────────────────────────────────────────

  onAlert(callback)    { this.onAlertCallback    = callback; return this }
  onClear(callback)    { this.onClearCallback    = callback; return this }
  onVerified(callback) { this.onVerifiedCallback = callback; return this }
  onReady(callback)    { this.onReadyCallback    = callback; return this }
  onError(callback)    { this.onErrorCallback    = callback; return this }

  initialize(meetingId, userRole) {
    this.meetingId = meetingId
    this.userRole  = userRole

    if (userRole === "interviewer") {
      console.log("[InterviewGuardService] Starting interviewer listener")
      this._startListening()
    }

    // Candidate: call launch() separately after consent
    return this
  }

  // ── CANDIDATE: launch app + wait for verification ─────────────────────────

  /**
   * Generates a challenge, fires the URI scheme to launch the app,
   * then listens to Firestore for interviewGuard.verified === true.
   * Calls onReady() when verified, onError() on timeout.
   */
  launch() {
    if (!this.meetingId) {
      console.error("[InterviewGuardService] No meetingId set")
      return
    }

    const challenge = generateChallenge()
    this._challenge = challenge
    sessionStorage.setItem("_igChallenge", challenge)

    // Fire URI scheme — OS delivers only to app on this machine
    const params = new URLSearchParams({
      challenge,
      meetingId:  this.meetingId,
      reportUrl:  REPORT_URL,
    })
    window.location.href = `interviewguard://verify?${params.toString()}`

    console.log("[InterviewGuardService] URI scheme fired — waiting for verification")

    // Start Firestore listener after brief delay (app needs time to open)
    setTimeout(() => this._waitForVerification(), 2000)
  }

  _waitForVerification() {
    // Timeout fallback
    this._timeoutTimer = setTimeout(() => {
      this._stopVerificationListener()
      console.warn("[InterviewGuardService] Verification timed out")
      this.onErrorCallback?.("timeout")
    }, LAUNCH_TIMEOUT)

    // Listen to room doc for interviewGuard.verified
    const roomRef = doc(db, "rooms", this.meetingId)
    this.unsubscribe = onSnapshot(roomRef, snapshot => {
      const data = snapshot.data()
      if (data?.interviewGuard?.verified === true) {
        this._stopVerificationListener()
        console.log("[InterviewGuardService] ✓ App verified — ready")
        this.onReadyCallback?.()
      }
    })
  }

  _stopVerificationListener() {
    clearTimeout(this._timeoutTimer)
    this._timeoutTimer = null
    this.unsubscribe?.()
    this.unsubscribe = null
  }

  // ── INTERVIEWER: listen to Firestore ─────────────────────────────────────

  _startListening() {
    if (!this.meetingId) return

    const roomRef    = doc(db, "rooms", this.meetingId)
    let prevVerified = false

    this.unsubscribe = onSnapshot(roomRef, snapshot => {
      const data = snapshot.data()
      if (!data?.interviewGuard) return

      const ig = data.interviewGuard

      // Fire onVerified once when app first verifies
      if (ig.verified && !prevVerified) {
        prevVerified = true
        console.log("[InterviewGuardService] App verified — monitoring started")
        this.onVerifiedCallback?.()
      }

      // Suspicious activity
      if (ig.suspiciousActivityDetected === true) {
        console.log("[InterviewGuardService] Suspicious activity:", ig.suspiciousActivityMessage)
        this.onAlertCallback?.(
          ig.suspiciousActivityMessage || "Suspicious activity detected on the candidate's device."
        )
      } else if (ig.suspiciousActivityDetected === false) {
        this.onClearCallback?.()
      }
    })
  }

  // ── End session ───────────────────────────────────────────────────────────

  /** Sets status=ended on room doc — app Firestore listener picks this up and terminates */
  async endSession() {
    if (!this.meetingId) return
    try {
      const roomRef = doc(db, "rooms", this.meetingId)
      await updateDoc(roomRef, { status: "ended" })
      console.log("[InterviewGuardService] Session ended — app will terminate")
    } catch (err) {
      console.error("[InterviewGuardService] Failed to end session:", err)
    }
  }

  /** Interviewer dismisses alert — resets the flag */
  async acknowledgeAlert() {
    if (!this.meetingId) return
    try {
      const roomRef = doc(db, "rooms", this.meetingId)
      await updateDoc(roomRef, { "interviewGuard.suspiciousActivityDetected": false })
    } catch (err) {
      console.error("[InterviewGuardService] Failed to acknowledge alert:", err)
    }
  }

  // ── Cleanup ───────────────────────────────────────────────────────────────

  cleanup() {
    this._stopVerificationListener()
    this.unsubscribe?.()
    this.unsubscribe        = null
    this.onAlertCallback    = null
    this.onClearCallback    = null
    this.onVerifiedCallback = null
    this.onReadyCallback    = null
    this.onErrorCallback    = null
    console.log("[InterviewGuardService] Cleaned up")
  }
}

export default InterviewGuardService